import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { ChatTeardropDots, X, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "How much for 3 bedrooms?",
  "Do you cover Brighton?",
  "How long does interior take?",
  "Are you fully insured?",
];

function getSessionId() {
  let s = localStorage.getItem("tmp_chat_session");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("tmp_chat_session", s);
  }
  return s;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { id: "intro", role: "assistant", text: "G'day! I'm Brushy, the assistant for The Melbourne Painters. Ask me anything — pricing, areas we cover, how to book." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message) return;
    const userId = crypto.randomUUID();
    setMsgs((m) => [...m, { id: userId, role: "user", text: message }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/chat", { session_id: sessionId.current, message });
      setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: r.data.reply }]);
    } catch (e) {
      void e;
      setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: "Sorry, I had a hiccup. Please call us on 0426 677 479 — Gurdeep will help right away." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-24 md:right-6 md:bottom-24 z-40 bg-[#1A1A1A] text-[#F2F2EF] border-2 border-[#111] shadow-brut p-4 hover:bg-[#D85C3A] transition-colors"
        aria-label="Open chat"
      >
        {open ? <X size={24} weight="bold" /> : <ChatTeardropDots size={24} weight="duotone" />}
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 right-4 md:right-6 md:bottom-44 z-40 w-[calc(100vw-2rem)] max-w-sm bg-[#F2F2EF] border-2 border-[#111] shadow-brut slide-up flex flex-col"
          style={{ height: "min(560px, calc(100vh - 9rem))" }}
        >
          <div className="bg-[#1A1A1A] text-[#F2F2EF] p-4 flex items-center gap-3 border-b-2 border-[#111]">
            <div className="w-9 h-9 bg-[#D85C3A] flex items-center justify-center border-2 border-[#F2F2EF]">
              <Sparkle size={18} weight="fill" />
            </div>
            <div>
              <div className="font-display text-lg uppercase tracking-tight">Brushy</div>
              <div className="text-[10px] uppercase tracking-widest text-[#999]">Live · usually replies in seconds</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div
                key={m.id}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`max-w-[85%] p-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#D85C3A] text-white border-2 border-[#111]"
                    : "bg-white border-2 border-[#111]"
                }`}
              >
                {m.text}
              </div>
            ))}
            {loading && (
              <div className="bg-white border-2 border-[#111] inline-block p-3 text-sm">
                <span className="inline-block w-2 h-2 bg-[#111] mr-1 animate-pulse" />
                <span className="inline-block w-2 h-2 bg-[#111] mr-1 animate-pulse" style={{ animationDelay: "120ms" }} />
                <span className="inline-block w-2 h-2 bg-[#111] animate-pulse" style={{ animationDelay: "240ms" }} />
              </div>
            )}
            <div ref={endRef} />
          </div>

          {msgs.length <= 1 && (
            <div className="p-3 border-t-2 border-[#111] flex flex-wrap gap-2 bg-[#EAEAE5]">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`chat-suggestion-${s.slice(0, 8)}`}
                  onClick={() => send(s)}
                  className="text-[10px] uppercase tracking-widest border-2 border-[#111] bg-white px-2 py-1 hover:bg-[#111] hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t-2 border-[#111] flex gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything..."
              className="flex-1 input-brut !py-2"
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-[#D85C3A] border-2 border-[#111] text-white p-3 disabled:opacity-50 hover:bg-[#111] transition-colors"
              aria-label="send"
            >
              <PaperPlaneTilt size={18} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
